/**
 * SoilSignOffPage — the manager's queue of completed soil application groups
 * awaiting sign-off (issue #417 Slice 4; rebuilt on primitives for U3).
 *
 * Gated by the fertilizing:signoff menu, which is why this page exists as a
 * navigable destination rather than a button buried on the session screen.
 *
 * The error branch returns BEFORE the loading guard: a failed load must render
 * a notice, not sit on "loading" forever (the S1b/S2 brick-on-load lesson).
 *
 * Search/ageing/bulk decisions all live in ./soilSignOff — this file is
 * presentation, mirroring the SoilGroupingPage (U2) shape.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, FilterBar, Notice, StatusPill } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { SoilWorkspaceContext } from './SoilWorkspaceLayout';
import { soilGroupService, SoilAwaitingGroup } from '../../services/soilGroupService';
import { formatMeasure } from '../../utils/formatMeasure';
import { formatDate } from '../../dateFormat';
import {
  SignOffRow, buildSignOffRows, filterSignOffRows, toBulkItems, failedLabels, WAITING_WARN_DAYS,
} from './soilSignOff';
import './nutrition.css';

const SoilSignOffPage: React.FC = () => {
  const { t } = useTranslation();
  // This page is a workspace TAB, so the layout hands it the resolved
  // programme. Null-safe because the suite renders it standalone, and because
  // useOutletContext returns null outside an <Outlet>.
  const workspace = useOutletContext<SoilWorkspaceContext | null>();
  const programId = workspace?.program?._id;
  const [items, setItems] = useState<SoilAwaitingGroup[] | null>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [search, setSearch] = useState('');
  const [overThresholdOnly, setOverThresholdOnly] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [partialFailed, setPartialFailed] = useState<string[]>([]);

  // Hooks must sit above every early return — a hook below a guard changes
  // hook order between renders and React throws.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    // Carry ?program= or the layout falls back to the tenant's ACTIVE
    // programme and the crumb returns the user to a different board (#429).
    { label: t('nutrition.soil.workspace.title'), to: programId ? `/nutrition/soil?program=${programId}` : '/nutrition/soil' },
    { label: t('nutrition.soil.workspace.tab.signOff') },
  ]);

  const load = useCallback(async () => {
    try {
      setItems(await soilGroupService.listAwaitingSignoff());
      setError('');
    } catch (_err) {
      setError(t('nutrition.soil.signOff.loadFailed'));
    }
    // t is intentionally omitted: react-i18next's `t` is not referentially
    // stable, and putting it in a dep array makes the effect re-fire on every
    // render (the S2 month-picker trap).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSignOff = async (row: SoilAwaitingGroup) => {
    setBusyId(row.id);
    setPartialFailed([]);
    try {
      await soilGroupService.signOffGroup(row.sessionId, row.groupId);
      await load();                      // re-fetch: never splice local state
      // The row is gone after reload, but its id would otherwise linger in
      // `selected` forever — no checkbox remains to untick it, and the bulk
      // bar would stay visible with a dead "Sign off selected" button.
      setSelected((prev) => prev.filter((id) => id !== row.id));
    } catch (_err) {
      setError(t('nutrition.soil.signOff.signOffFailed'));
    } finally {
      setBusyId('');
    }
  };

  if (error && items === null) {
    return <div className="pat-page"><Notice tone="danger">{error}</Notice></div>;
  }
  if (items === null) return <div className="pat-page">{t('common.loading')}</div>;

  const rows: SignOffRow[] = buildSignOffRows(items, new Date());
  const shown = filterSignOffRows(rows, search, overThresholdOnly);
  const shownIds = shown.map((r) => r.id);
  // Select-all must operate over the FILTERED rows, not the full set — a
  // manager who searched for one product must never bulk-sign the whole queue.
  const headerCheckedAll = shown.length > 0 && shown.every((r) => selected.includes(r.id));
  // A selection deliberately survives a search/filter change (so selecting a
  // row then narrowing the search doesn't silently drop it from the bulk
  // run) — but that means the bulk bar can include groups not currently on
  // screen. Surface the hidden count rather than pruning it, mirroring
  // SoilGroupingPage's hiddenSelectedCount (U2) for the same reason: an
  // undisclosed selection is worse here, because sign-off is irreversible.
  const hiddenSelectedCount = selected.filter((id) => !shownIds.includes(id)).length;

  const toggleRow = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    setSelected(headerCheckedAll ? [] : shownIds.slice());
  };

  const clearSelection = () => setSelected([]);

  const onBulkApply = async () => {
    const bulkItems = toBulkItems(rows, selected);
    if (!bulkItems.length) return;
    setBulkBusy(true);
    setError('');
    setPartialFailed([]);
    let threw = false;
    try {
      const res = await soilGroupService.signOffBulk(bulkItems);
      // `rows` is this render's snapshot — deliberately the PRE-reload rows,
      // since the failed groups are named from the set that was submitted.
      setPartialFailed(res.failCount > 0 ? failedLabels(rows, res.results) : []);
      setSelected([]);
    } catch (_err) {
      // Selection deliberately survives a thrown error so the user can retry
      // the same set without reselecting.
      threw = true;
    } finally {
      // Re-fetch on BOTH paths, never splice local state. A request that
      // reached the server but lost its response leaves rows on screen that
      // are already signed off — the display would lie until the next visit.
      await load();
      // load() clears `error` when it succeeds, so the bulk failure is set
      // AFTER it — otherwise the reload would silently wipe the message.
      if (threw) setError(t('nutrition.soil.signOff.bulkFailed'));
      setBulkBusy(false);
    }
  };

  return (
    <div className="pat-page" data-testid="soil-signoff-page">
      {error && <Notice tone="danger" testId="so-error">{error}</Notice>}
      {partialFailed.length > 0 && (
        <Notice tone="warn" testId="so-partial">
          {t('nutrition.soil.signOff.partial', { count: partialFailed.length, names: partialFailed.join(', ') })}
        </Notice>
      )}

      {items.length === 0 ? (
        <div className="ntr-empty">
          <div className="ntr-empty__message">{t('nutrition.soil.signOff.empty')}</div>
        </div>
      ) : (
        <div className="ntr-so-body">
          {selected.length > 0 && (
            <div className="ntr-so-bulk" data-testid="so-bulk-bar">
              <span>{t('nutrition.soil.signOff.selected', { count: selected.length })}</span>
              {hiddenSelectedCount > 0 && (
                <span data-testid="so-bulk-hidden">
                  {t('nutrition.soil.signOff.hiddenSelected', { count: hiddenSelectedCount })}
                </span>
              )}
              <button
                type="button" className="pat-btn pat-btn--primary"
                data-testid="so-bulk-apply" disabled={bulkBusy}
                onClick={onBulkApply}
              >
                {t('nutrition.soil.signOff.bulkApply')}
              </button>
              <button
                type="button" className="pat-btn pat-btn--subtle"
                data-testid="so-bulk-clear" onClick={clearSelection}
              >
                {t('nutrition.soil.signOff.clear')}
              </button>
            </div>
          )}

          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder={t('nutrition.soil.signOff.searchPlaceholder')}
            chips={[
              {
                id: 'overThreshold',
                label: t('nutrition.soil.signOff.overThreshold', { count: WAITING_WARN_DAYS }),
                active: overThresholdOnly,
              },
            ]}
            onChipClick={(chipId) => {
              if (chipId === 'overThreshold') setOverThresholdOnly((prev) => !prev);
            }}
          />

          <DataTable<SignOffRow>
            rows={shown}
            rowKey={(r) => r.id}
            emptyState={t('nutrition.soil.signOff.noMatches')}
            columns={[
              {
                key: 'check',
                // label is ReactNode, so the select-all lives in the header where it belongs.
                label: (
                  <input
                    type="checkbox"
                    data-testid="so-check-all"
                    checked={headerCheckedAll}
                    onChange={toggleAll}
                    aria-label={t('nutrition.soil.signOff.selectAll')}
                  />
                ),
                render: (r) => (
                  <input
                    type="checkbox"
                    data-testid={`so-check-${r.id}`}
                    checked={selected.includes(r.id)}
                    onChange={() => toggleRow(r.id)}
                    aria-label={t('nutrition.soil.signOff.selectRow', { label: r.label })}
                  />
                ),
              },
              { key: 'productName', label: t('nutrition.soil.signOff.col.product') },
              { key: 'month', label: t('nutrition.soil.signOff.col.period') },
              {
                key: 'label',
                label: t('nutrition.soil.signOff.col.group'),
                // Sign-off is irreversible, so the queue must offer a way to
                // inspect what is being approved rather than asking a manager
                // to accept a row of numbers on trust.
                render: (r) => (
                  <Link to={`/nutrition/soil/sessions/${r.sessionId}`} data-testid={`so-open-${r.id}`}>
                    {r.label}
                  </Link>
                ),
              },
              { key: 'orchards', label: t('nutrition.soil.signOff.col.orchards'), render: (r) => r.orchardNames.join(', ') },
              { key: 'plannedKg', label: t('nutrition.soil.signOff.col.kg'), align: 'right', render: (r) => formatMeasure(r.plannedKg) },
              { key: 'completedAt', label: t('nutrition.soil.signOff.col.completed'), render: (r) => (r.completedAt ? formatDate(r.completedAt) : '—') },
              { key: 'ecReading', label: t('nutrition.soil.signOff.col.ec'), render: (r) => r.ecReading ?? '—' },
              { key: 'signatureName', label: t('nutrition.soil.signOff.col.signature'), render: (r) => r.signatureName || '—' },
              {
                key: 'waiting',
                label: t('nutrition.soil.signOff.col.waiting'),
                render: (r) => (
                  // StatusPill takes only variant/children and does not spread
                  // props, so the testid goes on a wrapper span instead.
                  <span data-testid={`so-waiting-${r.id}`}>
                    <StatusPill variant={r.tone}>
                      {t('nutrition.soil.signOff.waiting', { count: r.waitingDays })}
                    </StatusPill>
                  </span>
                ),
              },
              {
                key: 'action',
                label: '',
                render: (r) => (
                  <button
                    type="button" className="pat-btn pat-btn--primary"
                    data-testid={`soil-signoff-action-${r.id}`}
                    disabled={busyId === r.id}
                    onClick={() => onSignOff(r)}
                  >
                    {t('nutrition.soil.signOff.action')}
                  </button>
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default SoilSignOffPage;
